"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/homepage", label: "Home" },
    { href: "/services", label: "Services" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (href: string) => pathname === href;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 pt-6 px-4 sm:px-8">
      <div
        className={`glass-card max-w-7xl mx-auto rounded-full px-4 sm:px-6 py-4 flex items-center justify-between transition-all duration-300 ${
          isScrolled ? "shadow-2xl" : ""
        }`}
      >
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <div className="bg-primary p-1.5 rounded-lg group-hover:bg-secondary transition-colors">
            <Icon name="FilmIcon" size={20} className="text-white" />
          </div>
          <span className="font-display font-semibold text-lg tracking-tight text-white hidden sm:block">
            Sunanda Entertainment
          </span>
          <span className="font-display font-semibold text-lg tracking-tight text-white sm:hidden">
            Sunanda
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={`transition-colors hover:text-white ${
                isActive(link.href) ? "text-white border-b-2 border-primary pb-1" : ""
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Link
            href="/contact"
            className="hidden sm:inline-flex items-center gap-2 bg-gradient-to-r from-primary to-red-800 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-all duration-300 glow-primary hover:glow-primary-hover"
          >
            Book Now
            <Icon name="ArrowRightIcon" size={16} />
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={isMobileMenuOpen ? "XMarkIcon" : "Bars3Icon"} size={20} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden glass-card max-w-7xl mx-auto mt-4 rounded-3xl p-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block text-base font-medium transition-colors hover:text-white ${
                isActive(link.href) ? "text-white" : "text-muted-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-red-800 px-5 py-3 rounded-full text-sm font-medium text-white w-full"
          >
            Book Now
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      )}
    </nav>
  );
}